// SPDX-License-Identifier: GPL-3.0-or-later
// The game surface on a touch screen. This module stops the browser from using a touch on the
// game surface: page scroll, pinch zoom, double-tap zoom, text selection and the long-press menu.
// It acts on the surface element only. The dialogs and the rest of the page keep their scroll
// and zoom. It also tells the page when the first gesture of the player ends, so the page can
// unlock the audio (a browser lets sound start only after a gesture).

/** The part of a browser event that this module reads. */
export interface SurfaceEvent {
  readonly cancelable?: boolean;
  /** The element that got the event first. The draft panel check reads it. */
  readonly target?: unknown;
  preventDefault(): void;
}

/** Options of a listener. Touch listeners must not be passive, or preventDefault does nothing. */
export interface SurfaceListenerOptions {
  readonly passive?: boolean;
}

/** The part of the game surface element that this module uses. */
export interface SurfaceTarget {
  addEventListener(
    type: string,
    listener: (ev: SurfaceEvent) => void,
    options?: SurfaceListenerOptions,
  ): void;
  removeEventListener(
    type: string,
    listener: (ev: SurfaceEvent) => void,
    options?: SurfaceListenerOptions,
  ): void;
  setAttribute?(name: string, value: string): void;
}

/** Events that the surface cancels. Safari sends the three gesture events for a pinch. */
export const BLOCKED_EVENTS: readonly string[] = [
  "touchstart",
  "touchmove",
  "gesturestart",
  "gesturechange",
  "gestureend",
  "contextmenu",
  "dblclick",
  "selectstart",
];

/** The class of the draft panel root. The panel uses this constant to set its class. */
export const DRAFT_PANEL_CLASS = "draft-panel";

/** Events that stay open inside the draft panel. Every other blocked event stays blocked. */
const OPEN_IN_DRAFT: readonly string[] = ["touchstart", "touchmove"];

/**
 * True when the target is inside the draft panel. A target with no `closest` is not inside it.
 * The check is by class, so the panel keeps its own markup.
 */
export function insideDraftPanel(target: unknown): boolean {
  const node = target as { closest?: (selector: string) => unknown } | null | undefined;
  return typeof node?.closest === "function" && node.closest(`.${DRAFT_PANEL_CLASS}`) !== null;
}

/** Events that a browser counts as a user gesture for audio (a pointerup does not count). */
export const GESTURE_EVENTS: readonly string[] = ["touchend", "click"];

/** The attribute that the CSS uses to find the game surface. */
export const SURFACE_ATTRIBUTE = "data-touch-surface";

const NOT_PASSIVE: SurfaceListenerOptions = { passive: false };

/**
 * The listener that cancels one blocked event type. An event that a browser does not let a page
 * cancel is left alone. A touch on the draft panel is left alone for the open events, because a
 * cancelled touchstart stops the click on a draft card.
 */
function cancelListener(type: string): (ev: SurfaceEvent) => void {
  return (ev) => {
    if (ev.cancelable === false) return;
    if (OPEN_IN_DRAFT.includes(type) && insideDraftPanel(ev.target)) return;
    ev.preventDefault();
  };
}

/**
 * Cancel the default action of every touch on the surface. The listeners are not passive.
 * The result removes them. An event that a browser does not let a page cancel is left alone.
 */
export function blockGestures(surface: SurfaceTarget): () => void {
  const listeners = BLOCKED_EVENTS.map((type) => ({ type, listener: cancelListener(type) }));
  for (const { type, listener } of listeners) {
    surface.addEventListener(type, listener, NOT_PASSIVE);
  }
  surface.setAttribute?.(SURFACE_ATTRIBUTE, "true");
  return () => {
    for (const { type, listener } of listeners) {
      surface.removeEventListener(type, listener, NOT_PASSIVE);
    }
  };
}

/** A latch for the first gesture. A callback that comes after the gesture runs at once. */
export interface GestureLatch {
  /** Run `callback` once, at the first gesture. The result removes the callback. */
  onFirst(callback: () => void): () => void;
  /** Tell the latch that a gesture ended. Only the first call has an effect. */
  fire(): void;
  /** True after the first gesture. */
  readonly fired: boolean;
}

export function createGestureLatch(): GestureLatch {
  const waiting = new Set<() => void>();
  let done = false;
  return {
    onFirst(callback) {
      if (done) {
        callback();
        return () => {};
      }
      waiting.add(callback);
      return () => {
        waiting.delete(callback);
      };
    },
    fire() {
      if (done) return;
      done = true;
      const callbacks = [...waiting];
      waiting.clear();
      for (const callback of callbacks) callback();
    },
    get fired() {
      return done;
    },
  };
}

/** Tell the latch about each touchend and click on the surface. The result removes the listeners. */
export function listenForGestures(surface: SurfaceTarget, latch: GestureLatch): () => void {
  const fire = (): void => latch.fire();
  for (const type of GESTURE_EVENTS) surface.addEventListener(type, fire);
  return () => {
    for (const type of GESTURE_EVENTS) surface.removeEventListener(type, fire);
  };
}
